import "./AppointmentActionsDropdown.css";

import { useAutoAnimate } from "@formkit/auto-animate/react";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  appointmentQueryKeys,
  calendarQueryKeys,
  useAppointmentEdit,
  useAppointmentRefundPayment,
  useMutationRecurringAppointmentCancel,
  useMutationRecurringAppointmentEdit,
} from "@gymflow/api";
import { cn } from "@gymflow/helpers";
import { AppointmentDTO, CustomPaymentCategoryDTO } from "@gymflow/types";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useQueryClient } from "@tanstack/react-query";
import useScaModal from "apps/portal/src/hooks/useScaModal";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import { isAxiosError } from "axios";
import classNames from "classnames";
import { NO_AVAILABLE_SLOTS_ERROR_CODES } from "libs/helpers/src/constants/ManuallyHandledErrorCodes";
import { useCallback, useContext, useEffect, useState } from "react";

import { ModalContext, useClubSettings } from "../../../providers";
import { ApiResolverContext } from "../../../providers/ApiResolverProvider/ApiResolverContext";
import useGymflowModels from "../../../store";
import { Button, ChevronRightIcon, SlideSideBar } from "../../atoms";
import { OptionType } from "../../atoms/base/components/OptionsList/components/OptionItem";
import { ConfirmModal } from "../../templates";
import { NotAvailableModal } from "../NotAvailableModal/NotAvailableModal";
import { AppointmentNotes } from "./AppointmentNotes";
import { AppointmentPayment } from "./AppointmentPayment";
import { AppointmentSummary } from "./AppointmentSummary";
import { useHandleAppointmentPayment } from "./hooks/useHandleAppointmentPayment";
import { AppointmentRescheduleModal } from "./RescheduleModal";
import { RecurringAppointmentRescheduleModal } from "./RescheduleRecurringModal";

interface SideBarAppointmentProps {
  onClose: () => void;
  isOpen: boolean;
  value: AppointmentDTO;
  isLoading: boolean;
}

export type PaymentUnitType = "ONLINE" | "CREDITS" | "IN_PERSON" | undefined;

// TODO: need to refactor (whole file)
export function SideBarAppointment({
  onClose,
  isOpen,
  value,
  isLoading,
}: SideBarAppointmentProps) {
  const [parent] = useAutoAnimate();
  const { handlePayment } = useHandleAppointmentPayment();
  const [paymentMethodId, setPaymentMethodId] = useState<string>();
  const [inPersonPaymentMethod, setInPersonPaymentMethod] =
    useState<OptionType<CustomPaymentCategoryDTO>>();

  const [selectedTab, setSelectedTab] = useState<
    "SUMMARY" | "PAYMENT" | "NOTES"
  >("SUMMARY");
  const [paymentUnit, setPaymentUnit] = useState<PaymentUnitType>();

  const [promoCode, setPromoCode] = useState<string>();

  const [isTabOpen, setIsTabOpen] = useState(isOpen);
  useEffect(() => {
    setIsTabOpen(isOpen);
  }, [isOpen]);

  const showScaModal = useScaModal({
    asMember: false,
  });

  const queryClient = useQueryClient();

  return (
    <SlideSideBar
      isOpen={isTabOpen}
      hide={onClose}
      className="!w-[32rem]"
      isLoading={isLoading}
    >
      <div className="flex h-full max-h-full flex-col overflow-hidden">
        <div className="flex h-full max-h-full flex-col overflow-hidden">
          <div className="flex flex-col justify-between border-b border-gray-200 p-8">
            <div className="mb-1 flex flex-row items-center justify-between">
              <div className="text-xl font-semibold text-gray-900">
                {value.appointable.name}
              </div>

              <FontAwesomeIcon
                onClick={() => {
                  onClose();
                }}
                className="cursor-pointer text-xl text-gray-600"
                icon={faClose}
              />
            </div>
            <div className="text-sm font-medium text-gray-600">
              View, update and cancel an existing appointment.
            </div>

            <div className="mt-4 flex">
              <Button
                onClick={() => setSelectedTab("SUMMARY")}
                className={classNames(
                  "w-full !rounded-r-none focus:outline-none",
                  {
                    "bg-gray-100": selectedTab === "SUMMARY",
                  },
                )}
              >
                Summary
              </Button>
              <Button
                onClick={() => setSelectedTab("PAYMENT")}
                className={classNames(
                  "w-full !rounded-none focus:outline-none",
                  {
                    "bg-gray-100": selectedTab === "PAYMENT",
                  },
                )}
              >
                Payment
              </Button>
              <Button
                onClick={() => setSelectedTab("NOTES")}
                className={classNames(
                  "w-full !rounded-l-none focus:outline-none",
                  {
                    "bg-gray-100": selectedTab === "NOTES",
                  },
                )}
              >
                Notes
              </Button>
            </div>
          </div>

          <div
            ref={parent}
            className="overflow-y-auto border-b border-gray-200 py-4"
          >
            {selectedTab === "SUMMARY" && (
              <AppointmentSummary appointment={value} />
            )}
            {selectedTab === "PAYMENT" && (
              <AppointmentPayment
                appointment={value}
                setPaymentMethodId={setPaymentMethodId}
                paymentMethodId={paymentMethodId}
                setPaymentUnit={setPaymentUnit}
                paymentUnit={paymentUnit}
                setPromoCode={setPromoCode}
                promoCode={promoCode}
                setInPersonPaymentMethod={setInPersonPaymentMethod}
                inPersonPaymentMethod={inPersonPaymentMethod}
              />
            )}
            {selectedTab === "NOTES" && (
              <AppointmentNotes
                appointmentId={value.id}
                noteList={value.noteList}
                setIsTabOpen={setIsTabOpen}
              />
            )}
          </div>
        </div>

        <div className="flex h-20 flex-row items-center justify-end gap-2 border-t border-gray-200 px-6">
          <AppointmentActionsDropdown
            className="flex-1"
            appointment={value}
            setIsTabOpen={setIsTabOpen}
          />
          <Button
            intent="primary"
            className={classNames("flex-1", {
              hidden:
                value.paymentStatus === "PAID" || selectedTab === "PAYMENT",
            })}
            onClick={() => setSelectedTab("PAYMENT")}
          >
            Checkout
          </Button>
          <Button
            intent="primary"
            className={classNames("flex-1", {
              hidden:
                value.paymentStatus === "PAID" || selectedTab !== "PAYMENT",
            })}
            disabled={
              !paymentUnit ||
              (paymentUnit === "ONLINE" && !paymentMethodId) ||
              (paymentUnit === "IN_PERSON" && !inPersonPaymentMethod?.value)
            }
            onClick={async () => {
              const response = await handlePayment({
                appointmentId: value.id,
                paymentUnit,
                paymentMethodId,
                promotionCode: promoCode,
                inPersonPaymentMethod,
              });

              if (response?.invoiceNumber) {
                await showScaModal({
                  invoiceNumber: String(response?.invoiceNumber),
                  userMemberId: value.appointmentUser.id,
                  onComplete: () => {
                    queryClient.invalidateQueries({
                      queryKey: appointmentQueryKeys.all(),
                    });
                    queryClient.invalidateQueries({
                      queryKey: calendarQueryKeys.appointments(),
                    });
                  },
                });
              }

              queryClient.invalidateQueries({
                queryKey: appointmentQueryKeys.all(),
              });
              queryClient.invalidateQueries({
                queryKey: calendarQueryKeys.appointments(),
              });
              setIsTabOpen(true);
            }}
          >
            Take Payment
          </Button>
        </div>
      </div>
    </SlideSideBar>
  );
}

export function AppointmentActionsDropdown({
  className,
  appointment,
  setIsTabOpen,
}: {
  className?: string;
  appointment: AppointmentDTO;
  setIsTabOpen: (newValue: boolean) => void;
}) {
  const { setModal, hide: hideModal } = useContext(ModalContext);
  const { toast } = useContext(ToastContext);

  const { club } = useClubSettings();
  const { clubId } = useContext(ApiResolverContext);
  const { api } = useGymflowModels();

  const editAppointmentMutation = useAppointmentEdit({
    api,
    tz: club.timezone,
  });

  const rescheduleRecurringAppointment = useMutationRecurringAppointmentEdit({
    api,
  });

  const refundAppointmentMutation = useAppointmentRefundPayment({
    api,
  });

  const cancelRecurringAppointment = useMutationRecurringAppointmentCancel({
    api,
  });

  const rescheduleCallback = useCallback(() => {
    setIsTabOpen(false);
    setModal(
      <AppointmentRescheduleModal
        appointment={appointment}
        onConfirm={async (newValues) => {
          try {
            await editAppointmentMutation.mutateAsync({
              appointmentId: appointment.id,
              patchedFields: newValues,
            });

            hideModal();
            setIsTabOpen(true);
          } catch (e: any) {
            hideModal();
            if (
              isAxiosError(e) &&
              NO_AVAILABLE_SLOTS_ERROR_CODES.includes(
                e.response?.data.error_code,
              )
            ) {
              setModal(
                <NotAvailableModal
                  errorCode={e.response?.data.error_code}
                  onConfirm={async () => {
                    await editAppointmentMutation.mutateAsync({
                      appointmentId: appointment.id,
                      patchedFields: {
                        ...newValues,
                        availabilityValidationIgnored: true,
                      },
                    });
                    hideModal();
                    setIsTabOpen(true);

                    toast({
                      message: "Appointment rescheduled.",
                    });
                  }}
                  onCancel={() => {
                    hideModal();
                    setIsTabOpen(true);
                  }}
                />,
              );
            }
          }
        }}
        onCancel={() => {
          hideModal();
          setIsTabOpen(true);
        }}
      />,
    );
  }, [
    appointment,
    editAppointmentMutation,
    hideModal,
    toast,
    setIsTabOpen,
    setModal,
  ]);

  const cancelCallback = useCallback(
    async (newStatus: "CANCELLED" | "LATE_CANCELLED") => {
      await editAppointmentMutation.mutateAsync({
        appointmentId: appointment.id,
        patchedFields: { status: newStatus },
      });

      if (
        ((appointment.finalPrice !== 0 &&
          appointment.finalPrice !== undefined) ||
          (appointment.finalSessionCost !== 0 &&
            appointment.finalSessionCost !== undefined)) &&
        appointment.paymentStatus === "PAID"
      ) {
        setIsTabOpen(false);
        setModal(
          <ConfirmModal
            title="Issue Refund?"
            cancelText="No"
            confirmText="Yes"
            onCancel={() => {
              hideModal();
              setIsTabOpen(true);
              toast({ message: "Appointment cancelled." });
            }}
            onConfirm={async () => {
              await refundAppointmentMutation.mutateAsync({
                clubId: clubId,
                appointmentId: appointment.id,
              });

              hideModal();
              setIsTabOpen(true);
              toast({
                message: "Appointment cancelled and refunded.",
              });
            }}
          >
            Would you like to refund the payment or credits for this
            appointment.
          </ConfirmModal>,
        );
      }
    },
    [
      appointment.finalPrice,
      appointment.finalSessionCost,
      appointment.id,
      appointment.paymentStatus,
      editAppointmentMutation,
      hideModal,
      toast,
      refundAppointmentMutation,
      setIsTabOpen,
      setModal,
      clubId,
    ],
  );

  const optionClassNames =
    "flex justify-between p-2.5 cursor-pointer hover:bg-gray-50 text-sm font-medium";

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <div
          className={cn(
            "flex rounded-lg border border-gray-300 px-4 py-2.5",
            className,
          )}
        >
          <div className="flex-1 text-center">More Options</div>
          <div className="size-6">
            <ChevronDownIcon fontSize={20} />
          </div>
        </div>
      </DropdownMenu.Trigger>

      <DropdownMenu.Content
        className="DropdownMenuContent bg-gray-0 rounded border border-gray-200"
        sideOffset={5}
      >
        {appointment.paymentStatus === "PAID" && (
          <DropdownMenu.Item
            className={cn("text-error-700", optionClassNames)}
            onSelect={async () => {
              await refundAppointmentMutation.mutateAsync({
                clubId: clubId,
                appointmentId: appointment.id,
              });
              toast({ message: "Appointment refunded." });
            }}
          >
            Refund
          </DropdownMenu.Item>
        )}
        {appointment.recurringAppointmentId === undefined &&
          appointment.kisiRoleAssignmentId === undefined && (
            <DropdownMenu.Item
              className={optionClassNames}
              onSelect={rescheduleCallback}
            >
              Reschedule
            </DropdownMenu.Item>
          )}
        {appointment.recurringAppointmentId !== undefined &&
          appointment.kisiRoleAssignmentId === undefined && (
            <DropdownMenu.Sub>
              <DropdownMenu.SubTrigger className={optionClassNames}>
                Reschedule
                <div>
                  <ChevronRightIcon pathClassName="stroke-[#667085] h-5 w-5" />
                </div>
              </DropdownMenu.SubTrigger>
              <DropdownMenu.SubContent
                className="bg-gray-0 rounded border border-gray-200"
                sideOffset={2}
                alignOffset={-5}
              >
                <DropdownMenu.Item
                  className={optionClassNames}
                  onSelect={rescheduleCallback}
                >
                  Only this appointment
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  className={optionClassNames}
                  onSelect={() => {
                    setIsTabOpen(false);
                    setModal(
                      <RecurringAppointmentRescheduleModal
                        appointment={appointment}
                        onConfirm={async (newValues) => {
                          setModal(
                            <ConfirmModal
                              type="warning"
                              onConfirm={async () => {
                                await rescheduleRecurringAppointment.mutateAsync(
                                  {
                                    recurringAppointmentId:
                                      appointment.recurringAppointmentId!,
                                    patchedFields: {
                                      ...newValues,
                                      fromAppointmentId: appointment.id,
                                    },
                                  },
                                );
                                hideModal();
                                setIsTabOpen(true);
                              }}
                              onCancel={() => {
                                hideModal();
                                setIsTabOpen(true);
                              }}
                              title="Attention"
                              confirmText="I got it"
                            >
                              If you have changed specific appointments in this
                              series, your changes will be undone and those
                              appointments will be consistent with the series
                              again.
                            </ConfirmModal>,
                          );
                        }}
                        onCancel={() => {
                          hideModal();
                          setIsTabOpen(true);
                        }}
                      />,
                    );
                  }}
                >
                  This and all future appointments in this series
                </DropdownMenu.Item>
              </DropdownMenu.SubContent>
            </DropdownMenu.Sub>
          )}
        {appointment.recurringAppointmentId === undefined && (
          <DropdownMenu.Item
            className={optionClassNames}
            onSelect={() => cancelCallback("CANCELLED")}
          >
            Cancel
          </DropdownMenu.Item>
        )}

        {appointment.recurringAppointmentId !== undefined && (
          <DropdownMenu.Sub>
            <DropdownMenu.SubTrigger className={optionClassNames}>
              Cancel
              <div>
                <ChevronRightIcon pathClassName="stroke-[#667085] h-5 w-5" />
              </div>
            </DropdownMenu.SubTrigger>
            <DropdownMenu.SubContent
              className="bg-gray-0 rounded border border-gray-200"
              sideOffset={2}
              alignOffset={-5}
            >
              <DropdownMenu.Item
                className={optionClassNames}
                onSelect={() => cancelCallback("CANCELLED")}
              >
                Only this appointment
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className={optionClassNames}
                onSelect={() => {
                  setIsTabOpen(false);
                  setModal(
                    <ConfirmModal
                      type="warning"
                      onConfirm={async () => {
                        await cancelRecurringAppointment.mutateAsync({
                          fromAppointmentId: appointment.id,
                          recurringAppointmentId:
                            appointment.recurringAppointmentId!,
                        });
                        hideModal();
                        toast({ message: "Appointment series cancelled." });

                        setIsTabOpen(true);
                      }}
                      title="Cancelling recurring appointment"
                      onCancel={() => {
                        hideModal();
                        setIsTabOpen(true);
                      }}
                    >
                      Are you sure you want to cancel all following instances of
                      this recurring appointment?
                    </ConfirmModal>,
                  );
                }}
              >
                This and all future appointments in this series
              </DropdownMenu.Item>
            </DropdownMenu.SubContent>
          </DropdownMenu.Sub>
        )}

        <DropdownMenu.Item
          className={optionClassNames}
          onSelect={async () => {
            await editAppointmentMutation.mutateAsync({
              appointmentId: appointment.id,
              patchedFields: { status: "NO_SHOW" },
            });
            toast({
              message: "Appointment marked as no show.",
            });
          }}
        >
          No Show
        </DropdownMenu.Item>
        <DropdownMenu.Item
          className={optionClassNames}
          onSelect={() => cancelCallback("LATE_CANCELLED")}
        >
          Late Cancel
        </DropdownMenu.Item>
        <DropdownMenu.Item
          className={optionClassNames}
          onSelect={async () => {
            await editAppointmentMutation.mutateAsync({
              appointmentId: appointment.id,
              patchedFields: { status: "ATTENDED" },
            });
            toast({
              message: "Appointment checked in.",
            });
          }}
        >
          Check In
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}
